/**
 * A sample work programme.
 *
 * Deliberately a REAL-SHAPED register: four jobs scheduled, two deferred for
 * money, and four the platform refused to decide. A mock that scheduled
 * everything would let a screen ship that treats the third bucket as a
 * footnote, which is the one thing this feature exists to prevent.
 *
 * The undecidable rows are split across two desks on purpose, because the
 * screen's job is to turn a list of eleven missing facts into two phone calls.
 */
import type { Programme } from "../domain/programme";

const at = (days: number, hour: number) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  d.setUTCHours(hour, 0, 0, 0);
  return d.toISOString();
};

export function mockProgramme(fiscalYear: number, envelope = 6_000_000): Programme {
  const scheduled = [
    {
      id: "P-1",
      title: "Seal TWY B, chainage 400–1300",
      subject: "TWY-B",
      cost: 1_020_000,
      escalationAvoided: 612_000,
      from: at(2, 1),
      to: at(2, 5),
      whyThen: "clear of traffic on TWY-B and within the weather limits for 'crack_sealing'",
      weatherBasis: ["observation to 06:00", "forecast issued 18 h ahead"],
    },
    {
      id: "P-2",
      title: "Joint reseal, apron north",
      subject: "A-NORTH",
      cost: 1_160_000,
      escalationAvoided: 164_000,
      from: at(3, 0),
      to: at(3, 4),
      whyThen: "clear of traffic on A-NORTH and within the weather limits for 'joint_seal'",
      weatherBasis: ["observation to 06:00"],
    },
    {
      id: "P-3",
      title: "Resurface stand A12",
      subject: "A12",
      cost: 1_940_000,
      escalationAvoided: 448_000,
      from: at(6, 2),
      to: at(6, 6),
      whyThen: "clear of traffic on A12 and within the weather limits for 'overlay'",
      weatherBasis: ["forecast issued 30 h ahead"],
    },
    {
      id: "P-4",
      title: "Replace AGL series, TWY C",
      subject: "TWY-C",
      cost: 880_000,
      escalationAvoided: 0,
      from: at(9, 1),
      to: at(9, 5),
      whyThen: "clear of traffic on TWY-C and within the weather limits for 'agl_replacement'",
      weatherBasis: ["observation to 06:00"],
    },
  ];

  const deferred = [
    {
      id: "P-5",
      title: "Rubber removal, RWY 17 touchdown",
      costNow: 1_400_000,
      escalation: 336_000,
      why: "the envelope is spent. Waiting a year on this one costs 336,000 more than doing it now.",
    },
    {
      id: "P-6",
      title: "Reseal apron south expansion joints",
      costNow: 720_000,
      escalation: 82_000,
      why:
        "every window this job could use is already taken by another job on the same position — the crew, " +
        "not the estate, is the constraint here",
    },
  ];

  const undecidable = [
    {
      id: "P-7",
      title: "Seal TWY A, chainage 0–900",
      costNow: 430_000,
      missing: "the candidate names neither a stand nor a zone, so there is no position to test a closure against",
      whoSupplies: "whoever raised the candidate",
      unblocks: "430,000 of work that is otherwise funded and ready to schedule",
    },
    {
      id: "P-8",
      title: "Recoat mandatory sign SGN-114",
      costNow: undefined,
      missing: "no unit rate for 'sign_recoat' at this site",
      whoSupplies: "whoever owns the term contract or rate schedule",
      unblocks: "ranking this candidate against the others at all",
    },
    {
      id: "P-9",
      title: "Repaint stand markings, apron east",
      costNow: 260_000,
      missing: "no weather limits are on file for 'line_marking', so no window can be tested",
      whoSupplies: "whoever owns the term contract or rate schedule",
      unblocks: "260,000 of work that is otherwise funded and ready to schedule",
    },
    {
      id: "P-10",
      title: "Pavement section predicted to fall below intervention PCI — PAV-014",
      costNow: undefined,
      missing: "no treatment category on the candidate",
      whoSupplies: "whoever raised the candidate",
      unblocks: "ranking this candidate against the others at all",
    },
  ];

  const scheduledCost = scheduled.reduce((t, j) => t + j.cost, 0);
  const blocked = undecidable.reduce((t, u) => t + (u.costNow ?? 0), 0);

  return {
    available: true,
    fiscalYear,
    currency: "SAR",
    envelope,
    searched: { from: at(0, 0), to: at(21, 0), days: 21 },
    scheduled,
    deferred,
    undecidable,
    totals: {
      scheduled: scheduled.length,
      scheduledCost,
      deferred: deferred.length,
      deferredEscalation: deferred.reduce((t, d) => t + (d.escalation ?? 0), 0),
      undecidable: undecidable.length,
      envelopeUnspent: Math.max(0, envelope - scheduledCost),
      blockedByMissingFacts: blocked,
    },
    rankedBy:
      "escalation avoided per unit of money committed. Not by severity and not by a weighted score: " +
      "spending is a financial act, and this is the order that avoids the most future cost per riyal. " +
      "A candidate whose escalation rate is unknown cannot enter this ranking and is listed as " +
      "undecidable rather than ranked last.",
    note:
      "4 of 10 candidates are scheduled, 2 deferred, 4 could not be decided at all. The undecidable ones " +
      "are the finding: each names one missing fact, and none of them is scheduled, deferred or dropped " +
      "— a programme that quietly omitted them would surface them later as emergencies. 690,000 of that " +
      "is already priced and funded, and is held up by a missing fact rather than by money.",
    horizonNote:
      "Slots were searched over 21 days. Traffic beyond that is a schedule nobody has published yet, so " +
      "a programme reaching further would be describing a plan rather than testing against one.",
  };
}
